/**
 * CommunityLogBoard — Iter-175 · Phase 6
 *
 * Read-only PM feed of resident trash / recycle confirmations for a
 * single property. Filters (date + status) run client-side. Polls
 * every 15 seconds so new confirmations show up without a manual
 * refresh. PMs see only entries for the property they manage.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { FileText, RefreshCw, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function statusBadgeCls(status) {
  const s = (status || '').toLowerCase();
  if (s === 'collected' || s === 'picked_up') return 'bg-emerald-600 text-white';
  if (s === 'in progress' || s === 'in_progress') return 'bg-blue-600 text-white';
  return 'bg-zinc-200 text-zinc-800';
}

function normalizeStatus(status) {
  const s = (status || '').toLowerCase();
  if (s === 'collected' || s === 'picked_up') return 'Collected';
  if (s === 'in progress' || s === 'in_progress') return 'In Progress';
  return 'Pending';
}

export default function CommunityLogBoard({ propertyId, propertyName }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [lastLoaded, setLastLoaded] = useState(null);
  // Iter-178 · Phase 11 — Send to Owner modal state.
  const [sendOpen, setSendOpen] = useState(false);
  const [sendFmt, setSendFmt] = useState('pdf');
  const [sendTo, setSendTo] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sendBody, setSendBody] = useState('');
  const [sendBusy, setSendBusy] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/ops/community-log/${propertyId}`,
                             { credentials: 'include' });
      if (!r.ok) {
        setError(`Could not load community log (${r.status})`);
      } else {
        const data = await r.json();
        setEntries(Array.isArray(data?.entries) ? data.entries : []);
        setError(null);
        setLastLoaded(new Date());
      }
    } catch (e) {
      setError('Network error loading community log');
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const exportLog = async (fmt) => {
    if (!propertyId) return;
    try {
      const params = new URLSearchParams({ format: fmt });
      if (dateFilter) params.set('date', dateFilter);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const r = await fetch(
        `${API}/api/ops/community-log/${propertyId}/export?${params.toString()}`,
        { credentials: 'include' },
      );
      if (!r.ok) {
        setError(`Export failed (${r.status})`);
        return;
      }
      const blob = await r.blob();
      const disposition = r.headers.get('content-disposition') || '';
      const match = /filename="?([^"]+)"?/i.exec(disposition);
      const fname = match ? match[1]
                          : `NECS-CommunityLog-${propertyId}.${fmt}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) {
      setError('Network error during export');
    }
  };

  const sendToOwner = async () => {
    if (!sendTo || !propertyId) return;
    setSendBusy(true);
    setSendResult(null);
    try {
      const r = await fetch(`${API}/api/community-log/email`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: propertyId,
          to: sendTo,
          format: sendFmt,
          date: dateFilter || null,
          status: statusFilter !== 'all' ? statusFilter : null,
          subject: sendSubject,
          body: `<pre style="font-family:Inter,sans-serif;white-space:pre-wrap">${sendBody}</pre>`,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        setSendResult({ ok: false, message: err.detail || 'Send failed' });
      } else {
        const data = await r.json();
        setSendResult({
          ok: true,
          message: data.test_mode
            ? 'Resend not configured — export queued (test mode).'
            : `Sent to ${data.to}.`,
        });
      }
    } catch (_) {
      setSendResult({ ok: false, message: 'Network error' });
    } finally {
      setSendBusy(false);
    }
  };

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (dateFilter) {
        const d = (e.created_at || '').slice(0, 10);
        if (d !== dateFilter) return false;
      }
      if (statusFilter !== 'all') {
        const s = normalizeStatus(e.status);
        if (s.toLowerCase() !== statusFilter) return false;
      }
      return true;
    });
  }, [entries, dateFilter, statusFilter]);

  return (
    <Card className="p-4 sm:p-6 bg-white border border-zinc-300" data-testid="community-log-board">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="text-lg font-bold text-black flex items-center gap-2"
              style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.04em' }}>
            <FileText className="h-5 w-5 text-black" /> COMMUNITY LOG
          </h3>
          <p className="text-sm text-zinc-600 mt-1">
            Live resident trash &amp; recycle confirmations for
            {propertyName ? ` ${propertyName}` : ' this property'}. Read-only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => exportLog('csv')}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-black bg-white text-black text-xs font-bold hover:bg-zinc-100 disabled:opacity-60"
            data-testid="community-log-export-csv"
          >
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => exportLog('pdf')}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-black bg-black text-white text-xs font-bold hover:opacity-90 disabled:opacity-60"
            data-testid="community-log-export-pdf"
          >
            Export PDF
          </button>
          <button
            type="button"
            onClick={() => {
              setSendFmt('pdf');
              const range = dateFilter || new Date().toISOString().slice(0, 10);
              setSendSubject(`NECS Community Log — ${propertyName || 'Property'} — ${range}`);
              setSendBody(
                `Hi,\n\nAttached is the latest NECS Community Log for ${propertyName || 'your property'} covering ${range}.\n\n— Noble Empire Cleaning Services`,
              );
              setSendResult(null);
              setSendOpen(true);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#C9A962] bg-[#C9A962] text-black text-xs font-bold hover:opacity-90"
            data-testid="community-log-send-owner"
          >
            <Send className="h-3.5 w-3.5" />
            Send to Owner
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-black bg-white text-black text-xs font-bold hover:bg-zinc-100 disabled:opacity-60"
            data-testid="community-log-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4"
           data-testid="community-log-filters">
        <div>
          <label className="block text-xs font-bold text-black mb-1"
                 htmlFor="community-log-date">Date</label>
          <input
            id="community-log-date"
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="border border-black bg-white text-black text-sm rounded-md px-2 py-1.5"
            data-testid="community-log-date-filter"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-black mb-1"
                 htmlFor="community-log-status">Status</label>
          <select
            id="community-log-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-black bg-white text-black text-sm rounded-md px-2 py-1.5"
            data-testid="community-log-status-filter"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="in progress">In Progress</option>
            <option value="collected">Collected</option>
          </select>
        </div>
        {(dateFilter || statusFilter !== 'all') && (
          <button
            type="button"
            onClick={() => { setDateFilter(''); setStatusFilter('all'); }}
            className="text-xs font-bold underline text-black hover:text-[#C9A962]"
            data-testid="community-log-clear-filters"
          >
            Clear filters
          </button>
        )}
        {lastLoaded && (
          <span className="ml-auto text-[11px] text-zinc-500"
                data-testid="community-log-last-loaded">
            Updated {lastLoaded.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      {error && (
        <div className="mb-3 p-2 rounded-md border border-red-300 bg-red-50 text-red-700 text-xs font-semibold"
             data-testid="community-log-error">
          {error}
        </div>
      )}

      {/* Board */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="community-log-table">
          <thead>
            <tr className="border-b-2 border-black text-left">
              <th className="py-2 px-2 font-bold text-black text-xs uppercase tracking-wider">Unit</th>
              <th className="py-2 px-2 font-bold text-black text-xs uppercase tracking-wider">Submitted</th>
              <th className="py-2 px-2 font-bold text-black text-xs uppercase tracking-wider">Type</th>
              <th className="py-2 px-2 font-bold text-black text-xs uppercase tracking-wider">Trash</th>
              <th className="py-2 px-2 font-bold text-black text-xs uppercase tracking-wider">Recycle</th>
              <th className="py-2 px-2 font-bold text-black text-xs uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-6 text-center text-sm italic text-zinc-500"
                    data-testid="community-log-empty">
                  {loading ? 'Loading community log…' : 'No entries match the current filters.'}
                </td>
              </tr>
            ) : filtered.map((e) => {
              const s = normalizeStatus(e.status);
              const submitted = e.created_at ? new Date(e.created_at) : null;
              return (
                <tr key={e.log_id}
                    className="border-b border-zinc-200"
                    data-testid={`community-log-row-${e.log_id}`}>
                  <td className="py-2 px-2 font-bold text-black">{e.actor_unit || '—'}</td>
                  <td className="py-2 px-2 text-black">
                    {submitted ? submitted.toLocaleString([], {
                      month: 'short', day: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    }) : '—'}
                  </td>
                  <td className="py-2 px-2 text-black">{e.request_type || 'Trash'}</td>
                  <td className="py-2 px-2 text-black">{e.trash_bags || 0}</td>
                  <td className="py-2 px-2 text-black">{e.recycling_bags || 0}</td>
                  <td className="py-2 px-2">
                    <Badge className={`${statusBadgeCls(e.status)} text-xs font-bold`}
                           data-testid={`community-log-row-${e.log_id}-status`}>
                      {s}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Iter-178 · Phase 11 — Send to Owner modal. */}
      {sendOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          onClick={() => !sendBusy && setSendOpen(false)}
          data-testid="community-log-send-modal"
        >
          <div
            className="w-full max-w-md rounded-lg bg-white p-5 border-2 border-black"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-sm font-bold uppercase tracking-widest text-black mb-3">
              Send Community Log to Owner
            </h4>
            <div className="space-y-2 text-sm">
              <label className="block font-bold text-black text-xs">Format</label>
              <div className="flex gap-2">
                {['pdf', 'csv'].map((f) => (
                  <button key={f} type="button"
                          onClick={() => setSendFmt(f)}
                          className={`px-2 py-1 text-xs rounded border ${
                            sendFmt === f ? 'bg-black text-white' : 'bg-white text-black border-black'
                          }`}
                          data-testid={`community-log-send-fmt-${f}`}>
                    {f.toUpperCase()}
                  </button>
                ))}
              </div>
              <label className="block font-bold text-black text-xs">To (email)</label>
              <input
                type="email"
                value={sendTo}
                onChange={(e) => setSendTo(e.target.value)}
                className="w-full border border-black bg-white text-black text-sm rounded-md px-2 py-1.5"
                data-testid="community-log-send-to"
              />
              <label className="block font-bold text-black text-xs">Subject</label>
              <input
                type="text"
                value={sendSubject}
                onChange={(e) => setSendSubject(e.target.value)}
                className="w-full border border-black bg-white text-black text-sm rounded-md px-2 py-1.5"
                data-testid="community-log-send-subject"
              />
              <label className="block font-bold text-black text-xs">Body</label>
              <textarea
                rows={4}
                value={sendBody}
                onChange={(e) => setSendBody(e.target.value)}
                className="w-full border border-black bg-white text-black text-sm rounded-md px-2 py-1.5"
                data-testid="community-log-send-body"
              />
              {sendResult && (
                <p
                  className={`text-xs font-bold ${sendResult.ok ? 'text-emerald-700' : 'text-red-700'}`}
                  data-testid="community-log-send-result"
                >
                  {sendResult.message}
                </p>
              )}
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSendOpen(false)}
                disabled={sendBusy}
                className="text-xs font-bold px-3 py-1.5 rounded border border-black text-black hover:bg-zinc-100 disabled:opacity-60"
              >
                Close
              </button>
              <button
                type="button"
                onClick={sendToOwner}
                disabled={sendBusy || !sendTo}
                className="text-xs font-bold px-3 py-1.5 rounded bg-[#C9A962] text-black hover:bg-[#B8974E] disabled:opacity-60"
                data-testid="community-log-send-submit"
              >
                {sendBusy ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
